import { inject, Injectable, signal } from '@angular/core';
import { UserService } from '@core/user/user.service';
import { Apollo, gql } from 'apollo-angular';
import {
    catchError,
    distinctUntilChanged,
    EMPTY,
    interval,
    startWith,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import pkg from '../../../../package.json';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SettingEntry {
  key: string;
  value: unknown;
}

interface VersionSettingValue {
  bo: string;
  client: string;
  clinic: string;
}

interface SettingsFindAllResponse {
  SettingsFindAll: SettingEntry[];
}

// ─── GraphQL query ────────────────────────────────────────────────────────────

const SETTINGS_FIND_ALL = gql`
  query VersionCheckSettingsFindAll {
    SettingsFindAll {
      key
      value
    }
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5 * 60_000; // 5 minutes
const VERSION_KEY = 'VERSION';

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly _apollo      = inject(Apollo); // Settings uses the default Apollo client
  private readonly _userService = inject(UserService);

  readonly outOfDate     = signal<boolean>(false);
  readonly dismissed     = signal<boolean>(false);
  readonly localVersion  = signal<string>(pkg.version?.trim() ?? '');
  readonly remoteVersion = signal<string>('');

  private _destroy$ = new Subject<void>();

  constructor() {
    this._userService.user$
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe(user => {
        this.stop();
        if (user?.id) {
          this.start();
        }
      });
  }

  start(): void {
    interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        takeUntil(this._destroy$),
        switchMap(() =>
          this._apollo
            .query<SettingsFindAllResponse>({
              query: SETTINGS_FIND_ALL,
              fetchPolicy: 'network-only',
            })
            .pipe(catchError(() => EMPTY))
        )
      )
      .subscribe(({ data }) => {
        const settings: SettingEntry[] = data?.SettingsFindAll ?? [];
        const entry = settings.find(s => s.key === VERSION_KEY);
        if (!entry) return;

        const parsed       = entry.value as VersionSettingValue;
        const remoteVersion = parsed?.bo?.trim();
        const localVersion  = pkg.version?.trim();

        this.remoteVersion.set(remoteVersion ?? '');
        this.outOfDate.set(!!remoteVersion && remoteVersion !== localVersion);
      });
  }

  stop(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = new Subject<void>();
    this.outOfDate.set(false);
    this.dismissed.set(false);
    this.remoteVersion.set('');
  }

  dismiss(): void {
    this.dismissed.set(true);
  }
}
